import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { api, structureReportPath, type StructureDetail, type StructureWrite } from '../lib/api';
import { clearUnsavedChanges } from '../lib/UnsavedChanges';

type Permanence = 'temporary' | 'permanent';
type OilChoice = 'yes' | 'no';
type Mode = 'dimensions' | 'casting';

/**
 * Step 1 of the end-user wizard. Markup mirrors the legacy
 * `resources/jsx/App.jsx` page1 layout (Bootstrap 5 + Hyper theme):
 *   container-fluid > row > col-12 col-lg-1/5 d-grid pt-1 gap-2
 * with `btn-check` radios paired to `btn btn-outline-primary` labels.
 */
export function StructureConfigurePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  // Edit mode: the report's "Edit Filter Type" link supplies the structure id
  // plus its current temp/oil options so the user can adjust the same unit
  // instead of creating a new one (legacy parity with `edit_structure` step 1).
  const editId = params.get('id')?.trim() || null;
  const tempParam = params.get('temp');
  const oilParam = params.get('oil');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingQuery = useQuery({
    queryKey: ['structure', editId],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${editId}`),
    enabled: !!editId,
  });

  const [permanence, setPermanence] = useState<Permanence | null>(
    tempParam === 'true' ? 'temporary' : tempParam === 'false' ? 'permanent' : null,
  );
  const [oil, setOil] = useState<OilChoice | null>(
    tempParam === 'false' ? (oilParam === 'true' ? 'yes' : 'no') : null,
  );

  useEffect(() => {
    const existing = existingQuery.data;
    if (!existing) return;
    setPermanence(existing.permanent ? 'permanent' : 'temporary');
    setOil(existing.permanent ? (existing.oilFiltration ? 'yes' : 'no') : null);
  }, [existingQuery.data]);

  const ready =
    permanence === 'temporary' ||
    (permanence === 'permanent' && oil !== null);

  const updateStructure = async () => {
    const existing = existingQuery.data;
    if (!editId || !existing || !ready || !permanence || submitting) return;

    setSubmitting(true);
    setError(null);
    const body: StructureWrite = {
      nickname: existing.nickname,
      permanent: permanence === 'permanent',
      oilFiltration: permanence === 'permanent' && oil === 'yes',
      structureTypeId: existing.structureTypeId,
      grateStyleId: existing.grateStyleId,
      grateDiameter: existing.grateDiameter,
      frameClearOpeningDiameter: existing.frameClearOpeningDiameter,
      grateSizeLeftToRight: existing.grateSizeLeftToRight,
      grateSizeFrontToBack: existing.grateSizeFrontToBack,
      clearOpeningLeftToRight: existing.clearOpeningLeftToRight,
      clearOpeningFrontToBack: existing.clearOpeningFrontToBack,
      grateSizeLongSpan: existing.grateSizeLongSpan,
      grateSizeShortSpan: existing.grateSizeShortSpan,
      clearOpeningLongSpan: existing.clearOpeningLongSpan,
      clearOpeningShortSpan: existing.clearOpeningShortSpan,
      curbOpeningWidth: existing.curbOpeningWidth,
      catchBasinWallToWallInsideDimension: existing.catchBasinWallToWallInsideDimension,
      basinSize: existing.basinSize,
      extendedBottomHeavyDuty: existing.extendedBottomHeavyDuty,
      extendedBottomDepth: existing.extendedBottomDepth,
      extendedBottomWidth: existing.extendedBottomWidth,
      foundryId: existing.foundryId,
      foundryPartId: existing.foundryPartId,
      structureNotes: existing.structureNotes,
      isDraft: existing.isDraft,
    };

    try {
      const saved = await api.put<StructureDetail>(`/api/v1/structures/${editId}`, body);
      queryClient.setQueryData(['structure', editId], saved);
      clearUnsavedChanges();
      navigate(structureReportPath(saved.hashId, true));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update structure');
      setSubmitting(false);
    }
  };

  const choose = (mode: Mode) => {
    if (!ready || !permanence) return;
    const temp = permanence === 'temporary' ? 'true' : 'false';
    const oilFlag =
      permanence === 'permanent' && oil === 'yes' ? 'true' : 'false';
    const idParam = editId ? `&id=${editId}` : '';
    // Casting lookup mirrors the legacy tool: skip the drainage-family step and
    // go straight to the foundry/casting-number browser.
    if (mode === 'casting') {
      navigate(`/newstructure/casting?temp=${temp}&oil=${oilFlag}${idParam}`);
      return;
    }
    navigate(`/newstructure/select?temp=${temp}&oil=${oilFlag}&mode=${mode}${idParam}`);
  };

  return (
    <div className="container-fluid p-0">
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <div className="container-fluid" id="page1">
            <div className="row">
              <div className="col-12 text-center">
                <h1>Configure New Structure</h1>
                <p>Will this installation be temporary or permanent?</p>
              </div>
              <div className="col-12 col-lg-1" />
              <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                <input
                  type="radio"
                  className="btn-check"
                  name="permanent"
                  id="temporary"
                  autoComplete="off"
                  checked={permanence === 'temporary'}
                  onChange={() => {
                    setPermanence('temporary');
                    setOil(null);
                  }}
                />
                <label className="btn btn-outline-primary" htmlFor="temporary">
                  Temporary
                </label>
              </div>
              <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                <input
                  type="radio"
                  className="btn-check"
                  name="permanent"
                  id="permanent"
                  autoComplete="off"
                  checked={permanence === 'permanent'}
                  onChange={() => setPermanence('permanent')}
                />
                <label className="btn btn-outline-primary" htmlFor="permanent">
                  Permanent
                </label>
              </div>
              <div className="col-12 col-lg-1 d-flex justify-content-center text-center">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tt-permanence">Will your structure be temporary or&nbsp;permanent?</Tooltip>}
                >
                  <img
                    src="/images/ICON/Icon_Info.png"
                    alt=""
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </OverlayTrigger>
              </div>
            </div>

            {permanence === 'permanent' && (
              <div className="row">
                <div className="col-12">
                  <p className="text-center pt-3">Will you need oil removal?</p>
                </div>
                <div className="col-12 col-lg-1" />
                <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                  <input
                    type="radio"
                    className="btn-check"
                    name="oil_filtration"
                    id="WithOilFilter"
                    autoComplete="off"
                    checked={oil === 'yes'}
                    onChange={() => setOil('yes')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="WithOilFilter">
                    Yes
                  </label>
                </div>
                <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                  <input
                    type="radio"
                    className="btn-check"
                    name="oil_filtration"
                    id="WithoutOilFilter"
                    autoComplete="off"
                    checked={oil === 'no'}
                    onChange={() => setOil('no')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="WithoutOilFilter">
                    No
                  </label>
                </div>
                <div className="col-12 col-lg-1 d-flex justify-content-center text-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tt-oil">Does your application require hydrocarbon oil&nbsp;removal?</Tooltip>}
                  >
                    <img
                      src="/images/ICON/Icon_Info.png"
                      alt=""
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </OverlayTrigger>
                </div>
              </div>
            )}

            {ready && (
              <div className="row">
                <div className="col-12">
                  <p className="text-center pt-3">
                    After configuration, you will be given the option to add this structure to a new or existing project&nbsp;collection.
                  </p>
                </div>
                <div className="col-12 col-lg-1" />
                {editId ? (
                  <div className="col-12 col-lg-10 d-grid pt-1 gap-2">
                    <button
                      type="button"
                      onClick={updateStructure}
                      className="btn btn-primary"
                      disabled={!existingQuery.data || existingQuery.isFetching || submitting}
                    >
                      {submitting ? 'Updating Structure...' : 'Update Structure'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                      <button
                        type="button"
                        onClick={() => choose('dimensions')}
                        className="btn btn-primary"
                      >
                        Configure With Dimensions
                      </button>
                    </div>
                    <div className="col-12 col-lg-5 d-grid pt-1 gap-2">
                      <button
                        type="button"
                        onClick={() => choose('casting')}
                        className="btn btn-primary"
                      >
                        Configure From Casting&nbsp;Number
                      </button>
                    </div>
                  </>
                )}
                <div className="col-12 col-lg-1 d-flex justify-content-center text-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tt-configure">Configure a custom structure with grate and opening dimensions or search for a part using the foundry casting&nbsp;number</Tooltip>}
                  >
                    <img
                      src="/images/ICON/Icon_Info.png"
                      alt=""
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </OverlayTrigger>
                </div>
              </div>
            )}
            {error && <div className="alert alert-warning mt-2" role="alert">{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
